import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Menu,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  ViewColumn as ColumnsIcon,
  Search as SearchIcon,
  TrendingUp,
  AttachMoney,
  Assignment,
  ShowChart,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { PurchaseReturn } from '../../types/purchaseReturns.types';
import { purchaseReturnsService } from '../../services/purchaseReturns.service';
import { Supplier } from '../../types/suppliers.types';
import suppliersService from '../../services/suppliers.service';
import { Warehouse } from '../../types/warehouses.types';
import { warehousesService } from '../../services/warehouses.service';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { fmtUnitPrice } from '../../utils/formatNumber';

const translations = {
  en: {
    title: 'Purchase Returns Report',
    titleAr: 'تقرير مرتجعات المشتريات',
    supplier: 'Supplier',
    warehouse: 'Warehouse',
    allSuppliers: 'All Suppliers',
    allWarehouses: 'All Warehouses',
    allStatuses: 'All Statuses',
    status: 'Status',
    dateFrom: 'From Date',
    dateTo: 'To Date',
    search: 'Search...',
    today: 'Today',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    thisYear: 'This Year',
    detailedView: 'Detailed Returns',
    summaryBySupplier: 'Summary by Supplier',
    summaryByItem: 'Summary by Item',
    summaryByWarehouse: 'Summary by Warehouse',
    returnNumber: 'Return #',
    returnDate: 'Date',
    invoiceNumber: 'Invoice #',
    supplierName: 'Supplier',
    warehouseName: 'Warehouse',
    itemCode: 'Item Code',
    itemName: 'Item',
    unitName: 'Unit',
    quantity: 'Qty',
    unitPrice: 'Unit Price',
    totalPrice: 'Total',
    returnReason: 'Reason',
    notes: 'Notes',
    totalReturns: 'Total Returns',
    totalQuantity: 'Total Qty',
    totalAmount: 'Total Amount',
    avgReturnAmount: 'Avg Amount',
    draft: 'Draft',
    posted: 'Posted',
    stats: 'Statistics',
    returnsCount: 'Total Returns',
    itemsReturned: 'Items Returned',
    totalValue: 'Total Value',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    columns: 'Columns',
    filters: 'Filters',
    noData: 'No purchase returns found',
    loading: 'Loading...',
    kwd: 'KWD',
  },
  ar: {
    title: 'تقرير مرتجعات المشتريات',
    titleAr: 'تقرير مرتجعات المشتريات',
    supplier: 'المورد',
    warehouse: 'المخزن',
    allSuppliers: 'جميع الموردين',
    allWarehouses: 'جميع المخازن',
    allStatuses: 'جميع الحالات',
    status: 'الحالة',
    dateFrom: 'من تاريخ',
    dateTo: 'إلى تاريخ',
    search: 'بحث...',
    today: 'اليوم',
    thisWeek: 'هذا الأسبوع',
    thisMonth: 'هذا الشهر',
    lastMonth: 'الشهر الماضي',
    thisYear: 'هذه السنة',
    detailedView: 'المرتجعات التفصيلية',
    summaryBySupplier: 'ملخص حسب المورد',
    summaryByItem: 'ملخص حسب الصنف',
    summaryByWarehouse: 'ملخص حسب المخزن',
    returnNumber: 'رقم المرتجع',
    returnDate: 'التاريخ',
    invoiceNumber: 'رقم الفاتورة',
    supplierName: 'المورد',
    warehouseName: 'المخزن',
    itemCode: 'كود الصنف',
    itemName: 'الصنف',
    unitName: 'الوحدة',
    quantity: 'الكمية',
    unitPrice: 'سعر الوحدة',
    totalPrice: 'الإجمالي',
    returnReason: 'السبب',
    notes: 'ملاحظات',
    totalReturns: 'إجمالي المرتجعات',
    totalQuantity: 'إجمالي الكمية',
    totalAmount: 'إجمالي المبلغ',
    avgReturnAmount: 'متوسط المبلغ',
    draft: 'مسودة',
    posted: 'مرحّل',
    stats: 'الإحصائيات',
    returnsCount: 'عدد المرتجعات',
    itemsReturned: 'الأصناف المرتجعة',
    totalValue: 'القيمة الإجمالية',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    columns: 'الأعمدة',
    filters: 'الفلاتر',
    noData: 'لا توجد مرتجعات مشتريات',
    loading: 'جاري التحميل...',
    kwd: 'د.ك',
  },
};

interface DetailedReturn {
  returnNumber: string;
  returnDate: string;
  invoiceNumber?: string;
  supplierId?: number;
  supplierName: string;
  warehouseId: number;
  warehouseName: string;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  unitName: string;
  unitNameAr: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  status: string;
}

interface SupplierSummary {
  supplierId?: number;
  supplierName: string;
  supplierNameAr: string;
  returnsCount: number;
  totalQuantity: number;
  totalAmount: number;
}

interface ItemSummary {
  itemId: number;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  returnsCount: number;
  totalQuantity: number;
  avgPrice: number;
  totalAmount: number;
}

interface WarehouseSummary {
  warehouseId: number;
  warehouseName: string;
  warehouseNameAr: string;
  returnsCount: number;
  totalQuantity: number;
  totalAmount: number;
}

const PurchaseReturnsReport: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();

  const [returns, setReturns] = useState<PurchaseReturn[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  
  const [supplierFilter, setSupplierFilter] = useState<number | ''>('');
  const [warehouseFilter, setWarehouseFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [datePreset, setDatePreset] = useState<string>('');
  
  const [detailPage, setDetailPage] = useState(0);
  const [detailRowsPerPage, setDetailRowsPerPage] = useState(25);

  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);
  const [visibleColumns, setVisibleColumns] = useState({
    returnNumber: true,
    date: true,
    invoice: true,
    supplier: true,
    warehouse: true,
    item: true,
    quantity: true,
    price: true,
    total: true,
  });

  useEffect(() => {
    loadSuppliers();
    loadWarehouses();
    loadCompanyInfo();
  }, []);

  useEffect(() => {
    loadData();
    setDetailPage(0);
  }, [supplierFilter, warehouseFilter, statusFilter, dateFromFilter, dateToFilter]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await suppliersService.getAll();
      setSuppliers(data);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
    }
  };

  const loadWarehouses = async () => {
    try {
      const data = await warehousesService.getAll();
      setWarehouses(data);
    } catch (error) {
      console.error('Failed to load warehouses:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await purchaseReturnsService.getAll(
        statusFilter || undefined,
        supplierFilter || undefined,
        warehouseFilter || undefined,
        dateFromFilter || undefined,
        dateToFilter || undefined
      );
      setReturns(data);
    } catch (error) {
      console.error('Failed to load purchase returns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    const today = new Date();
    let fromDate = '';
    let toDate = today.toISOString().split('T')[0];

    switch(preset) {
      case 'today':
        fromDate = toDate;
        break;
      case 'thisWeek':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        fromDate = weekStart.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        break;
      case 'lastMonth':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        fromDate = lastMonth.toISOString().split('T')[0];
        toDate = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
        break;
      case 'thisYear':
        fromDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        break;
    }

    setDateFromFilter(fromDate);
    setDateToFilter(toDate);
  };

  const getDetailedData = (): DetailedReturn[] => {
    const detailed: DetailedReturn[] = [];
    
    returns.forEach(ret => {
      ret.details.forEach(detail => {
        detailed.push({
          returnNumber: ret.returnNumber,
          returnDate: ret.returnDate,
          invoiceNumber: ret.purchaseInvoiceNumber,
          supplierId: ret.supplierId,
          supplierName: isRTL ? (ret.supplierNameAr || ret.supplierName || '-') : (ret.supplierName || '-'),
          warehouseId: ret.warehouseId,
          warehouseName: isRTL ? (ret.warehouseNameAr || ret.warehouseName || '-') : (ret.warehouseName || '-'),
          itemCode: detail.itemCode,
          itemName: detail.itemName,
          itemNameAr: detail.itemNameAr,
          unitName: detail.unitName,
          unitNameAr: detail.unitNameAr,
          quantity: detail.quantity,
          unitPrice: detail.unitPrice,
          totalPrice: detail.totalPrice,
          status: ret.status || 'Draft',
        });
      });
    });

    return detailed;
  };

  const getFilteredData = () => {
    let filtered = getDetailedData();
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.returnNumber.toLowerCase().includes(query) ||
        r.supplierName.toLowerCase().includes(query) ||
        r.itemName.toLowerCase().includes(query) ||
        r.itemCode.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const getSummaryBySupplier = (): SupplierSummary[] => {
    const summary = new Map<string, SupplierSummary>();
    
    returns.forEach(ret => {
      const key = ret.supplierId?.toString() || 'unknown';
      if (!summary.has(key)) {
        summary.set(key, {
          supplierId: ret.supplierId,
          supplierName: ret.supplierName || '-',
          supplierNameAr: ret.supplierNameAr || '-',
          returnsCount: 0,
          totalQuantity: 0,
          totalAmount: 0,
        });
      }
      
      const s = summary.get(key)!;
      s.returnsCount++;
      ret.details.forEach(d => {
        s.totalQuantity += d.quantity;
        s.totalAmount += d.totalPrice;
      });
    });
    
    return Array.from(summary.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  };

  const getSummaryByItem = (): ItemSummary[] => {
    const summary = new Map<number, ItemSummary>();
    
    returns.forEach(ret => {
      ret.details.forEach(detail => {
        if (!summary.has(detail.itemId)) {
          summary.set(detail.itemId, {
            itemId: detail.itemId,
            itemCode: detail.itemCode,
            itemName: detail.itemName,
            itemNameAr: detail.itemNameAr,
            returnsCount: 0,
            totalQuantity: 0,
            avgPrice: 0,
            totalAmount: 0,
          });
        }
        
        const item = summary.get(detail.itemId)!;
        item.returnsCount++;
        item.totalQuantity += detail.quantity;
        item.totalAmount += detail.totalPrice;
      });
    });
    
    summary.forEach(item => {
      item.avgPrice = item.totalQuantity > 0 ? item.totalAmount / item.totalQuantity : 0;
    });
    
    return Array.from(summary.values()).sort((a, b) => b.totalQuantity - a.totalQuantity);
  };

  const getSummaryByWarehouse = (): WarehouseSummary[] => {
    const summary = new Map<number, WarehouseSummary>();
    
    returns.forEach(ret => {
      if (!summary.has(ret.warehouseId)) {
        summary.set(ret.warehouseId, {
          warehouseId: ret.warehouseId,
          warehouseName: ret.warehouseName || '-',
          warehouseNameAr: ret.warehouseNameAr || '-',
          returnsCount: 0,
          totalQuantity: 0,
          totalAmount: 0,
        });
      }
      
      const wh = summary.get(ret.warehouseId)!;
      wh.returnsCount++;
      ret.details.forEach(d => {
        wh.totalQuantity += d.quantity;
        wh.totalAmount += d.totalPrice;
      });
    });
    
    return Array.from(summary.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  };

  const getStatistics = () => {
    const detailed = getDetailedData();
    const uniqueItems = new Set(detailed.map(d => d.itemCode)).size;
    const totalAmount = detailed.reduce((sum, d) => sum + d.totalPrice, 0);
    
    return {
      returnsCount: returns.length,
      itemsReturned: uniqueItems,
      totalValue: totalAmount,
      avgValue: returns.length > 0 ? totalAmount / returns.length : 0,
    };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatCurrency = (amount: number) => {
    return amount.toFixed(3);
  };

  const getStatusColor = (status: string) => {
    return status === 'Posted' ? 'success' : 'warning';
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      // Logo
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({
            base64: companyInfo.logoBase64,
            extension: 'png',
          });
          worksheet.addImage(logoImage, {
            tl: { col: 0, row: 0 },
            ext: { width: 100, height: 80 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      // Company Header
      worksheet.mergeCells('C1:I1');
      const companyCell = worksheet.getCell('C1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 20, bold: true, color: { argb: 'FF1565C0' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('C2:I2');
      const addressCell = worksheet.getCell('C2');
      addressCell.value = companyInfo?.address || '';
      addressCell.font = { size: 10 };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('C3:I3');
      const contactCell = worksheet.getCell('C3');
      contactCell.value = `${t.kwd} | ${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9 };
      contactCell.alignment = { horizontal: 'center' };

      worksheet.getRow(4).height = 10;

      // Report Title
      worksheet.mergeCells('A5:L5');
      const titleCell = worksheet.getCell('A5');
      titleCell.value = isRTL ? t.titleAr : t.title;
      titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1565C0' }
      };

      const tabNames = [t.detailedView, t.summaryBySupplier, t.summaryByItem, t.summaryByWarehouse];
      worksheet.mergeCells('A6:L6');
      const tabCell = worksheet.getCell('A6');
      tabCell.value = `📊 ${tabNames[currentTab]}`;
      tabCell.font = { size: 12, bold: true, color: { argb: 'FF1565C0' } };
      tabCell.alignment = { horizontal: 'center' };

      worksheet.getRow(7).height = 10;

      if (currentTab === 0) {
        const data = getFilteredData();
        
        const headers = [
          t.returnNumber, t.returnDate, t.invoiceNumber, t.supplierName,
          t.warehouseName, t.itemCode, t.itemName, t.unitName,
          t.quantity, t.unitPrice, t.totalPrice, t.status
        ];
        
        const headerRow = worksheet.getRow(8);
        headers.forEach((header, i) => {
          const cell = headerRow.getCell(i + 1);
          cell.value = header;
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF1565C0' }
          };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
        
        let currentRow = 9;
        data.forEach((item, index) => {
          const row = worksheet.getRow(currentRow);
          row.getCell(1).value = item.returnNumber;
          row.getCell(2).value = formatDate(item.returnDate);
          row.getCell(3).value = item.invoiceNumber || '-';
          row.getCell(4).value = item.supplierName;
          row.getCell(5).value = item.warehouseName;
          row.getCell(6).value = item.itemCode;
          row.getCell(7).value = isRTL ? item.itemNameAr : item.itemName;
          row.getCell(8).value = isRTL ? item.unitNameAr : item.unitName;
          row.getCell(9).value = item.quantity;
          row.getCell(10).value = item.unitPrice;
          row.getCell(11).value = item.totalPrice;
          row.getCell(12).value = item.status;
          
          if (index % 2 === 0) {
            row.eachCell(cell => {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF5F5F5' }
              };
            });
          }
          
          currentRow++;
        });
        
        worksheet.columns = [
          { width: 16 }, { width: 14 }, { width: 16 }, { width: 25 },
          { width: 20 }, { width: 14 }, { width: 30 }, { width: 12 },
          { width: 12 }, { width: 14 }, { width: 14 }, { width: 12 }
        ];
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PurchaseReturns_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const exportToPdf = async () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;

      doc.setFillColor(245, 250, 255);
      doc.rect(0, 0, pageWidth, 45, 'F');

      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25);
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(21, 101, 192);
      doc.text(isRTL ? (companyInfo?.companyNameAr || '') : (companyInfo?.companyName || ''), pageWidth / 2, 15, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(companyInfo?.address || '', pageWidth / 2, 22, { align: 'center' });
      doc.text(`${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`, pageWidth / 2, 27, { align: 'center' });

      doc.setFillColor(21, 101, 192);
      doc.roundedRect(pageWidth / 2 - 60, 32, 120, 10, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text(isRTL ? t.titleAr : t.title, pageWidth / 2, 38.5, { align: 'center' });

      doc.setFontSize(9);
      doc.setTextColor(21, 101, 192);
      doc.text(new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB'), pageWidth - 15, 38.5, { align: 'right' });

      const tableHeaders = [
        t.returnDate,
        t.returnNumber,
        t.supplierName,
        t.itemName,
        t.quantity,
        t.unitPrice,
        t.totalPrice,
      ];

      const tableData: any[] = [];
      const data = getFilteredData();

      data.forEach((item) => {
        tableData.push([
          formatDate(item.returnDate),
          item.returnNumber,
          item.supplierName.substring(0, 20),
          (isRTL ? item.itemNameAr : item.itemName).substring(0, 25),
          formatCurrency(item.quantity),
          formatCurrency(item.unitPrice),
          formatCurrency(item.totalPrice),
        ]);
      });

      const stats = getStatistics();
      tableData.push([
        {
          content: 'TOTAL',
          colSpan: 4,
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 11,
            halign: 'center'
          }
        },
        {
          content: '',
          styles: { fillColor: [13, 71, 161] }
        },
        {
          content: '',
          styles: { fillColor: [13, 71, 161] }
        },
        {
          content: formatCurrency(stats.totalValue),
          styles: { fillColor: [13, 71, 161], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'center' }
        }
      ]);

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: 50,
        theme: 'striped',
        headStyles: {
          fillColor: [21, 101, 192],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [50, 50, 50]
        },
        alternateRowStyles: {
          fillColor: [248, 250, 251]
        },
      });

      doc.save(`PurchaseReturns_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const stats = getStatistics();

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ 
          background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 'bold'
        }}>
          {t.title}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }} className="no-print">
          <Tooltip title={t.refresh}>
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.columns}>
            <IconButton onClick={(e) => setColumnsAnchor(e.currentTarget)} color="primary">
              <ColumnsIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<ExcelIcon />}
            onClick={exportToExcel}
            sx={{ 
              background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)',
              color: 'white'
            }}
          >
            {t.exportExcel}
          </Button>
          <Button
            variant="contained"
            startIcon={<PdfIcon />}
            onClick={exportToPdf}
            sx={{ 
              background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
              color: 'white'
            }}
          >
            {t.exportPdf}
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ 
              background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)',
              color: 'white'
            }}
          >
            {t.print}
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }} className="no-print">
        <Card sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          transition: 'transform 0.3s',
          '&:hover': { transform: 'translateY(-5px)' }
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.returnsCount}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                  {stats.returnsCount}
                </Typography>
              </Box>
              <Assignment sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ 
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          transition: 'transform 0.3s',
          '&:hover': { transform: 'translateY(-5px)' }
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.itemsReturned}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                  {stats.itemsReturned}
                </Typography>
              </Box>
              <TrendingUp sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ 
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          transition: 'transform 0.3s',
          '&:hover': { transform: 'translateY(-5px)' }
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.totalValue}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                  {formatCurrency(stats.totalValue)}
                </Typography>
              </Box>
              <AttachMoney sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ 
          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          color: 'white',
          transition: 'transform 0.3s',
          '&:hover': { transform: 'translateY(-5px)' }
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.avgReturnAmount}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                  {formatCurrency(stats.avgValue)}
                </Typography>
              </Box>
              <ShowChart sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
          {t.filters}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {['today', 'thisWeek', 'thisMonth', 'lastMonth', 'thisYear'].map((preset) => (
            <Chip
              key={preset}
              label={t[preset as keyof typeof t]}
              onClick={() => handleDatePreset(preset)}
              color={datePreset === preset ? 'primary' : 'default'}
              variant={datePreset === preset ? 'filled' : 'outlined'}
            />
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
          <Autocomplete
            options={suppliers}
            getOptionLabel={(option) => isRTL ? option.supplierNameAr : option.supplierName}
            value={suppliers.find(s => s.supplierId === supplierFilter) || null}
            onChange={(_, value) => setSupplierFilter(value?.supplierId || '')}
            renderInput={(params) => (
              <TextField {...params} label={t.supplier} size="small" />
            )}
          />

          <Autocomplete
            options={warehouses}
            getOptionLabel={(option) => isRTL ? option.warehouseNameAr : option.warehouseName}
            value={warehouses.find(w => w.warehouseId === warehouseFilter) || null}
            onChange={(_, value) => setWarehouseFilter(value?.warehouseId || '')}
            renderInput={(params) => (
              <TextField {...params} label={t.warehouse} size="small" fullWidth />
            )}
          />

          <TextField
            select
            fullWidth
            size="small"
            label={t.status}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">{t.allStatuses}</MenuItem>
            <MenuItem value="Draft">{t.draft}</MenuItem>
            <MenuItem value="Posted">{t.posted}</MenuItem>
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.dateFrom}
              value={dateFromFilter ? new Date(dateFromFilter) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setDateFromFilter(newValue);
                if (dateToFilter && newValue && dateToFilter < newValue) setDateToFilter('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={minDate}
              maxDate={dateToFilter ? new Date(dateToFilter) : maxDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.dateTo}
              value={dateToFilter ? new Date(dateToFilter) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setDateToFilter(newValue);
                if (dateFromFilter && newValue && dateFromFilter > newValue) setDateFromFilter('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={dateFromFilter ? new Date(dateFromFilter) : minDate}
              maxDate={maxDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </LocalizationProvider>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder={t.search}
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setDetailPage(0); }}
          sx={{ mt: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }} className="no-print">
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t.detailedView} />
          <Tab label={t.summaryBySupplier} />
          <Tab label={t.summaryByItem} />
          <Tab label={t.summaryByWarehouse} />
        </Tabs>
      </Paper>

      {/* Print Header */}
      <Box className="print-only" sx={{ mb: 4 }}>
        <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            {companyInfo?.logoBase64 && (
              <Box>
                <img src={companyInfo.logoBase64} alt="Logo" style={{ height: '70px', maxWidth: '150px' }} />
              </Box>
            )}
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1565C0', mb: 1 }}>
                {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
              </Typography>
              <Typography variant="body1" sx={{ color: '#546E7A', mb: 0.5 }}>
                {companyInfo?.address || ''}
              </Typography>
              <Typography variant="body1" sx={{ color: '#546E7A' }}>
                📞 {companyInfo?.phone || ''} | 📧 {companyInfo?.email || ''}
              </Typography>
            </Box>
            <Box sx={{ width: '150px' }}></Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, background: 'linear-gradient(90deg, #1565C0 0%, #1976D2 100%)', color: 'white', textAlign: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {isRTL ? t.titleAr : t.title}
          </Typography>
          <Typography variant="subtitle1">
            {[t.detailedView, t.summaryBySupplier, t.summaryByItem, t.summaryByWarehouse][currentTab]}
          </Typography>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#546E7A' }}>
            📅 {new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
          <Typography variant="body2" sx={{ color: '#546E7A' }}>
            🕐 {new Date().toLocaleTimeString(isRTL ? 'ar-KW' : 'en-GB')}
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          {currentTab === 0 && (
            <>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                    {visibleColumns.returnNumber && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.returnNumber}</TableCell>}
                    {visibleColumns.date && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.returnDate}</TableCell>}
                    {visibleColumns.invoice && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.invoiceNumber}</TableCell>}
                    {visibleColumns.supplier && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.supplierName}</TableCell>}
                    {visibleColumns.warehouse && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.warehouseName}</TableCell>}
                    {visibleColumns.item && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>}
                    {visibleColumns.quantity && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.quantity}</TableCell>}
                    {visibleColumns.price && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.unitPrice}</TableCell>}
                    {visibleColumns.total && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalPrice}</TableCell>}
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredData()
                    .slice(detailPage * detailRowsPerPage, detailPage * detailRowsPerPage + detailRowsPerPage)
                    .map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:hover': { backgroundColor: 'action.hover' },
                          backgroundColor: index % 2 === 0 ? 'background.default' : 'action.hover'
                        }}
                      >
                        {visibleColumns.returnNumber && <TableCell>{row.returnNumber}</TableCell>}
                        {visibleColumns.date && <TableCell>{formatDate(row.returnDate)}</TableCell>}
                        {visibleColumns.invoice && <TableCell>{row.invoiceNumber || '-'}</TableCell>}
                        {visibleColumns.supplier && <TableCell>{row.supplierName}</TableCell>}
                        {visibleColumns.warehouse && <TableCell>{row.warehouseName}</TableCell>}
                        {visibleColumns.item && (
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {isRTL ? row.itemNameAr : row.itemName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.itemCode}
                            </Typography>
                          </TableCell>
                        )}
                        {visibleColumns.quantity && <TableCell align="right">{formatCurrency(row.quantity)}</TableCell>}
                        {visibleColumns.price && <TableCell align="right">{fmtUnitPrice(row.unitPrice)}</TableCell>}
                        {visibleColumns.total && <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(row.totalPrice)}</TableCell>}
                        <TableCell>
                          <Chip
                            label={row.status === 'Posted' ? t.posted : t.draft}
                            size="small"
                            color={getStatusColor(row.status)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  {getFilteredData().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 5 }}>
                        <Typography color="text.secondary">{t.noData}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={getFilteredData().length}
                page={detailPage}
                onPageChange={(_, newPage) => setDetailPage(newPage)}
                rowsPerPage={detailRowsPerPage}
                onRowsPerPageChange={(e) => { setDetailRowsPerPage(parseInt(e.target.value, 10)); setDetailPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                className="no-print"
              />
            </>
          )}

          {currentTab === 1 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.supplierName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.returnsCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalQuantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalAmount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.avgReturnAmount}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSummaryBySupplier().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: '500' }}>
                      {isRTL ? row.supplierNameAr : row.supplierName}
                    </TableCell>
                    <TableCell align="right">{row.returnsCount}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalQuantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      {formatCurrency(row.totalAmount)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.returnsCount > 0 ? row.totalAmount / row.returnsCount : 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {currentTab === 2 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.returnsCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalQuantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.avgReturnAmount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalAmount}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSummaryByItem().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        {isRTL ? row.itemNameAr : row.itemName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.itemCode}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.returnsCount}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalQuantity)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.avgPrice)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      {formatCurrency(row.totalAmount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {currentTab === 3 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.warehouseName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.returnsCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalQuantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalAmount}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSummaryByWarehouse().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: '500' }}>
                      {isRTL ? row.warehouseNameAr : row.warehouseName}
                    </TableCell>
                    <TableCell align="right">{row.returnsCount}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalQuantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      {formatCurrency(row.totalAmount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      {/* Column Visibility Menu */}
      <Menu
        anchorEl={columnsAnchor}
        open={Boolean(columnsAnchor)}
        onClose={() => setColumnsAnchor(null)}
      >
        {Object.keys(visibleColumns).map((key) => (
          <MenuItem key={key}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleColumns[key as keyof typeof visibleColumns]}
                  onChange={(e) => setVisibleColumns({
                    ...visibleColumns,
                    [key]: e.target.checked
                  })}
                />
              }
              label={key}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          @page {
            margin: 1cm;
            size: landscape;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          /* Header Styles */
          table thead tr {
            background: linear-gradient(135deg, #1565C0 0%, #1976D2 100%) !important;
            -webkit-print-color-adjust: exact;
          }
          
          table thead th {
            background: linear-gradient(135deg, #1565C0 0%, #1976D2 100%) !important;
            color: white !important;
            font-weight: bold !important;
            padding: 12px 8px !important;
            border: 1px solid #1565C0 !important;
            -webkit-print-color-adjust: exact;
          }
          
          /* Row Styles */
          table tbody tr:nth-child(odd) {
            background-color: #F5F9FF !important;
            -webkit-print-color-adjust: exact;
          }
          
          table tbody tr:nth-child(even) {
            background-color: #FFFFFF !important;
            -webkit-print-color-adjust: exact;
          }
          
          table tbody td {
            padding: 10px 8px !important;
            border: 1px solid #E3F2FD !important;
            color: #263238 !important;
            -webkit-print-color-adjust: exact;
          }
          
          /* Chip Styles */
          .MuiChip-root {
            -webkit-print-color-adjust: exact;
          }
          
          .MuiChip-colorSuccess {
            background-color: #4CAF50 !important;
            color: white !important;
            -webkit-print-color-adjust: exact;
          }
          
          .MuiChip-colorWarning {
            background-color: #FF9800 !important;
            color: white !important;
            -webkit-print-color-adjust: exact;
          }
          
          /* Table Container */
          .MuiTableContainer-root {
            box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
            border-radius: 8px !important;
            overflow: hidden !important;
          }
          
          /* Print Header */
          .print-only h4, .print-only h3, .print-only h5 {
            color: #1565C0 !important;
            -webkit-print-color-adjust: exact;
          }
          
          .print-only .MuiPaper-root {
            -webkit-print-color-adjust: exact;
            box-shadow: none !important;
          }
          
          .print-only img {
            -webkit-print-color-adjust: exact;
          }
        }
        
        @media screen {
          .print-only {
            display: none !important;
          }
        }
      `}</style>
    </Box>
  );
};

export default PurchaseReturnsReport;